import moment from "moment";
import { Station } from "~/api/models/station.model";
import { AvoltaAlarmConfiguration } from "~/configs/alarms/avolta-alarm.config";
import { AvoltaAlarm, AvoltaAlarmEnum } from "~/configs/alarms/avolta-alarm.model";
import { AvoltaIndicator } from "~/configs/indicators/avolta-indicators.config";
import { getAlarmFromValues } from '~/misc/values-utils';

/**
 * Check if station is in a gas alarm state
 * @param station 
 * @returns {boolean} true if station is in a gas alarm  state
 */
export function isStationInGasAlarm(station: Station) {
    return !!station.latest_values[AvoltaIndicator.CH4_concentration_alarm];
}

/**
 * Check if station is in a trouble alarm state (eol, battery low, system failure, etc)
 * @param station 
 * @returns {boolean} true if is in a trouble alarm state
 */
export function isStationInTrouble(station: Station): boolean {
    const alarms = getStationAlarms(station).filter(a => a?.name !== AvoltaAlarmEnum.offline);
    return alarms?.length ? alarms.some((a: AvoltaAlarm) => a?.name !== AvoltaAlarmEnum.CH4_concentration_alarm) : false;
}

/**
 * Check if station is offline. Device is considered offline only if it is not considered "really" offline
 * @param station 
 * @returns {boolean}
 */
export function isStationOffline(station: Station): boolean {
    return station?.status === 'offline';
}

/**
 * Check if station is really offline. Really offline means a device is offline for more than two weeks
 * @param station 
 * @returns 
 */
export function isStationReallyOffline(station: Station): boolean {
    return !station.last_seen || moment(station.last_seen * 1000).isBefore(moment().subtract(2, 'weeks'));
}

export function isStationOk(station: Station): boolean {
    return !isStationInGasAlarm(station) && !isStationInTrouble(station) && !isStationOffline(station) && !isStationReallyOffline(station);
}

/**
 * Return list of all current alarms for this station
 * @param station 
 * @returns 
 */
export function getStationAlarms(station: Station): AvoltaAlarm[] {
    const values = station?.latest_values || {};

    const alarms = getAlarmFromValues(values).map(a => AvoltaAlarmConfiguration[a]);

    if(isStationOffline(station)) {
        alarms.push(AvoltaAlarmConfiguration.offline);
    }
    
    return alarms;
}

/**
 * Return the most significant alarm for this station
 * @param station 
 * @returns 
 */
export function getStationAlarm(station: Station): AvoltaAlarm {
    const alarms = getStationAlarms(station);

    let alarm = alarms.find(a => a.name === AvoltaAlarmEnum?.CH4_concentration_alarm);

    if(alarm) {
        return alarm;
    }

    alarm = alarms.find(a => a.name === AvoltaAlarmEnum?.CH4_system_failure);

    if(alarm) {
        return alarm;
    }

    
    alarm = alarms.find(a => a.name === AvoltaAlarmEnum?.CH4_eol_alarm);

    if(alarm) {
        return alarm;
    }

    
    alarm = alarms.find(a => a.name === AvoltaAlarmEnum?.CH4_battery_alarm);

    if(alarm) {
        return alarm;
    }

    
    alarm = alarms.find(a => a.name === AvoltaAlarmEnum?.offline);

    if(alarm) {
        return alarm;
    }

}

